import { useEffect, useState } from 'preact/hooks';
import {
  platformAgreements,
  KindRow,
  PolicyVersionSummary,
} from './adminApi';

// AgreementsPage — platform-admin UI for the clickwrap acceptance
// engine. See docs/flows/AGREEMENTS.md.
//
// Three things in v1:
//   1. Kinds table — every kind + current version + acceptance count.
//   2. Drill-down: click a kind, see full version history with stats.
//   3. Publish New Version modal: kind dropdown, auto-bumped version,
//      title, markdown textarea, required toggle, target user types.
//      Step-up gated; the modal handles the StepUpModal redirect when
//      the server returns "step-up re-auth required".
//
// Out of scope (not v1):
//   - Per-user acceptance browser (privacy + not useful at UI level)
//   - Markdown live preview (later, if useful)
//   - Diff highlighting between versions

export function AgreementsPage() {
  const [kinds, setKinds] = useState<KindRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openKind, setOpenKind] = useState<string | null>(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError(null);
    platformAgreements
      .listKinds()
      .then((resp) => setKinds(resp.kinds || []))
      .catch((err: any) => setError(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Agreements</h1>
        <button
          onClick={() => setPublishModalOpen(true)}
          style={publishBtnStyle}
        >
          Edit / Publish new version
        </button>
      </div>
      <p style={{ color: 'var(--admin-text-dim)', marginTop: 0 }}>
        Versioned policy documents (Terms of Service, Privacy Policy,
        and future addenda). Click <em>Edit / Publish new version</em>
        to open the editor pre-filled with the current document; saving
        auto-bumps the version and forces every existing user to
        re-accept on next sign-in. Prior versions remain in the audit
        trail forever (DB rows are insert-only post-publish).
      </p>

      {error && (
        <div style={errorBoxStyle}>
          {error}{' '}
          <button onClick={refresh} style={{ marginLeft: 8 }}>
            Retry
          </button>
        </div>
      )}

      {loading && !error && <div>Loading…</div>}

      {!loading && !error && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Kind</th>
              <th style={thStyle}>Title</th>
              <th style={thStyle}>Current ver.</th>
              <th style={thStyle}>Required</th>
              <th style={thStyle}>Audience</th>
              <th style={thStyleRight}>Acceptances</th>
              <th style={thStyleRight}>Versions</th>
              <th style={thStyle}>Effective</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => (
              <KindTableRow
                key={k.kind}
                row={k}
                isOpen={openKind === k.kind}
                onToggle={() =>
                  setOpenKind((cur) => (cur === k.kind ? null : k.kind))
                }
              />
            ))}
            {kinds.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: 'var(--admin-text-dim)', padding: 16 }}>
                  No agreement kinds published yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {publishModalOpen && (
        <PublishModal
          kinds={kinds}
          onClose={() => setPublishModalOpen(false)}
          onPublished={() => {
            setPublishModalOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─── KindTableRow + version-history drill-down ─────────────────────

function KindTableRow({
  row,
  isOpen,
  onToggle,
}: {
  row: KindRow;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const cur = row.current;
  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <td style={tdStyle}>
          <code>{row.kind}</code>
          <span style={{ marginLeft: 8, color: 'var(--admin-text-dim)' }}>
            {isOpen ? '▼' : '▶'}
          </span>
        </td>
        <td style={tdStyle}>{cur?.title || <em style={{ color: 'var(--admin-text-dim)' }}>(not yet published)</em>}</td>
        <td style={tdStyle}>{cur ? `v${cur.version}` : '—'}</td>
        <td style={tdStyle}>{cur ? (cur.required ? '✓' : '—') : '—'}</td>
        <td style={tdStyle}>
          {cur && cur.targetUserTypes && cur.targetUserTypes.length > 0
            ? cur.targetUserTypes.join(', ')
            : <span style={{ color: 'var(--admin-text-dim)' }}>all</span>}
        </td>
        <td style={tdStyleRight}>{row.acceptanceCountCurrent}</td>
        <td style={tdStyleRight}>{row.totalVersions}</td>
        <td style={tdStyle}>{cur ? formatDate(cur.effectiveAt) : '—'}</td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--admin-surface-raised)', padding: 16 }}>
            <VersionHistory kind={row.kind} />
          </td>
        </tr>
      )}
    </>
  );
}

function VersionHistory({ kind }: { kind: string }) {
  const [versions, setVersions] = useState<PolicyVersionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    platformAgreements
      .versionHistory(kind)
      .then((resp) => setVersions(resp.versions || []))
      .catch((err: any) => setError(err?.message || 'Failed to load'));
  }, [kind]);

  if (error) return <div style={{ color: 'var(--admin-error, #c0392b)' }}>{error}</div>;
  if (!versions) return <div>Loading version history…</div>;
  if (versions.length === 0) return <div>No versions found.</div>;

  return (
    <table style={{ ...tableStyle, marginTop: 0 }}>
      <thead>
        <tr>
          <th style={thStyle}>Version</th>
          <th style={thStyle}>Title</th>
          <th style={thStyle}>Required</th>
          <th style={thStyle}>Audience</th>
          <th style={thStyleRight}>Acceptances</th>
          <th style={thStyleRight}>Body size</th>
          <th style={thStyle}>Effective</th>
          <th style={thStyle}>Superseded</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((v) => (
          <tr key={v.id} style={v.supersededAt ? { color: 'var(--admin-text-dim)' } : {}}>
            <td style={tdStyle}>v{v.version}</td>
            <td style={tdStyle}>{v.title}</td>
            <td style={tdStyle}>{v.required ? '✓' : '—'}</td>
            <td style={tdStyle}>
              {v.targetUserTypes && v.targetUserTypes.length > 0
                ? v.targetUserTypes.join(', ')
                : 'all'}
            </td>
            <td style={tdStyleRight}>{v.acceptanceCount}</td>
            <td style={tdStyleRight}>{(v.bodyLengthBytes / 1024).toFixed(1)} KB</td>
            <td style={tdStyle}>{formatDate(v.effectiveAt)}</td>
            <td style={tdStyle}>
              {v.supersededAt ? formatDate(v.supersededAt) : <strong style={{ color: 'var(--admin-success, #2d7a3a)' }}>current</strong>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Publish modal ─────────────────────────────────────────────────

// NEW_KIND_SENTINEL — picked from the dropdown to surface the free-text
// input for adding a brand-new kind. Keeps the dropdown's default UX
// (one-click for existing kinds) while making "add a new kind" a
// first-class option that just-works (no schema migration, no code
// change — the engine reads target_user_types per-row at runtime).
const NEW_KIND_SENTINEL = '__new_kind__';

function PublishModal({
  kinds,
  onClose,
  onPublished,
}: {
  kinds: KindRow[];
  onClose: () => void;
  onPublished: () => void;
}) {
  // Existing kinds come from ListKinds. Adding a new one just means
  // typing a name in the field that appears when "(new kind…)" is
  // selected — the engine is data-driven, so a new row IS a new kind.
  const existingKinds = kinds.map((k) => k.kind);
  // Default to the first existing kind so the modal opens in "edit
  // existing" mode for the most common case.
  const [kindSelection, setKindSelection] = useState(
    existingKinds.length > 0 ? existingKinds[0] : NEW_KIND_SENTINEL,
  );
  const [newKindName, setNewKindName] = useState('');
  // Effective kind: either the dropdown selection, or the typed new
  // name when the dropdown is set to NEW_KIND_SENTINEL.
  const kind = kindSelection === NEW_KIND_SENTINEL ? newKindName.trim() : kindSelection;

  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [required, setRequired] = useState(true);
  const [targetConsumer, setTargetConsumer] = useState(false);
  const [targetPhotographer, setTargetPhotographer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-bump preview: derived from the kinds table data.
  const currentRow = kinds.find((k) => k.kind === kind);
  const nextVersion = (currentRow?.current?.version || 0) + 1;

  // When the kind changes, pre-fill EVERYTHING from the current
  // version so the modal naturally functions as the "edit existing"
  // surface — open kind, see the live document, edit the textarea,
  // submit auto-bumps to v(current+1). Stripe / Apple / Adobe all
  // expose document editing this way: the new-version form IS the
  // editor for the existing one.
  // Re-derive form state when the user picks a different kind from
  // the dropdown. Deliberately keyed on `kindSelection` (the dropdown
  // value), NOT on `kind` (which changes on every keystroke into the
  // new-kind text input) — otherwise the effect would reset
  // title/body to '' on every character typed when adding a new kind.
  useEffect(() => {
    if (currentRow?.current) {
      setRequired(currentRow.current.required);
      setTargetConsumer(currentRow.current.targetUserTypes?.includes('consumer') || false);
      setTargetPhotographer(currentRow.current.targetUserTypes?.includes('photographer') || false);
      setTitle(currentRow.current.title);
      // Pre-fill body — this is the "edit and auto-version" workflow.
      // bodyMd comes from ListKinds for the current version only;
      // empty for first publish of a brand-new kind.
      setBodyMd(currentRow.current.bodyMd || '');
    } else {
      // Brand-new kind, no prior version. Sensible defaults — leave
      // audience as nothing-selected so the operator deliberately
      // chooses; defaults to all-authenticated-users (empty array)
      // unless they tick a box.
      setRequired(true);
      setTargetConsumer(false);
      setTargetPhotographer(false);
      setTitle('');
      setBodyMd('');
    }
  }, [kindSelection, currentRow]);

  const handlePublish = async () => {
    setSubmitting(true);
    setError(null);
    const targets: string[] = [];
    if (targetConsumer) targets.push('consumer');
    if (targetPhotographer) targets.push('photographer');
    try {
      await platformAgreements.publish({
        kind,
        version: 0, // server auto-bumps
        title: title.trim(),
        bodyMd,
        required,
        targetUserTypes: targets,
      });
      onPublished();
    } catch (err: any) {
      const msg = err?.message || 'Publish failed';
      // Recognize the step-up message so the operator knows to refresh.
      if (msg.toLowerCase().includes('step-up')) {
        setError('Step-up re-auth required. Click your name in the header → Step Up, then try again.');
      } else {
        setError(msg);
      }
      setSubmitting(false);
    }
  };

  // Valid when: kind is non-empty (either an existing one or a typed
  // new name), title has content, body has content. The new-kind text
  // input pre-sanitizes to lowercase/underscore so server-side rejection
  // for bad names is unlikely.
  const valid =
    kind.length > 0 &&
    title.trim().length > 0 &&
    bodyMd.trim().length > 0;

  // Editor mode: are we editing an existing kind, or seeding a new one?
  const isEditingExisting = !!currentRow?.current;
  const heading = isEditingExisting
    ? `Edit ${kind} (publishes as v${nextVersion})`
    : `Publish first version of ${kind}`;

  return (
    <div style={modalBackdropStyle} role="dialog" aria-modal="true">
      <div style={modalBoxStyle}>
        <h2 style={{ margin: '0 0 12px' }}>{heading}</h2>
        <p style={{ color: 'var(--admin-text-dim)', marginTop: 0, fontSize: 14 }}>
          {isEditingExisting
            ? `The textarea below shows the live v${currentRow!.current!.version} text. Edit it; saving publishes a new version (v${nextVersion}) and forces every existing user to re-accept on next sign-in. The prior version stays in the audit trail forever.`
            : 'Seeding the first version of this kind. No existing users will be re-prompted (they have nothing to compare against yet).'}{' '}
          Step-up gated, audit-logged.
        </p>

        <Field label="Kind">
          <select
            value={kindSelection}
            onChange={(e: any) => setKindSelection(e.currentTarget.value)}
            style={inputStyle}
          >
            {existingKinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
            <option value={NEW_KIND_SENTINEL}>(new kind…)</option>
          </select>
          {kindSelection === NEW_KIND_SENTINEL && (
            <input
              value={newKindName}
              onInput={(e: any) =>
                setNewKindName(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))
              }
              placeholder="lowercase_with_underscores e.g. data_retention_addendum"
              style={{ ...inputStyle, marginTop: 6 }}
            />
          )}
        </Field>

        <Field label={`Version (auto-bumped to v${nextVersion})`}>
          <input value={`v${nextVersion}`} readOnly style={{ ...inputStyle, background: 'var(--admin-surface-raised)' }} />
        </Field>

        <Field label="Title">
          <input
            value={title}
            onInput={(e: any) => setTitle(e.currentTarget.value)}
            placeholder="e.g., Terms of Service"
            style={inputStyle}
          />
        </Field>

        <Field label="Body (Markdown)">
          <textarea
            value={bodyMd}
            onInput={(e: any) => setBodyMd(e.currentTarget.value)}
            placeholder="# Title&#10;&#10;Markdown body…"
            rows={14}
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13 }}
          />
        </Field>

        <Field label="Required (gates user access)">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={required} onChange={(e: any) => setRequired(e.currentTarget.checked)} />
            <span>Block sign-in until accepted</span>
          </label>
        </Field>

        <Field label="Audience (empty = all authenticated users)">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 16 }}>
            <input type="checkbox" checked={targetConsumer} onChange={(e: any) => setTargetConsumer(e.currentTarget.checked)} />
            <span>Consumer</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={targetPhotographer} onChange={(e: any) => setTargetPhotographer(e.currentTarget.checked)} />
            <span>Photographer</span>
          </label>
        </Field>

        {error && <div style={errorBoxStyle}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={submitting} style={cancelBtnStyle}>
            Cancel
          </button>
          <button onClick={handlePublish} disabled={!valid || submitting} style={publishBtnStyle}>
            {submitting ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 13, color: 'var(--admin-text-dim)', marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toISOString().split('T')[0];
  } catch {
    return iso;
  }
}

// ─── Styles ────────────────────────────────────────────────────────

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  marginTop: 16,
};
const thStyle = {
  textAlign: 'left' as const,
  padding: '8px 12px',
  borderBottom: '1px solid var(--admin-border)',
  fontSize: 13,
  fontWeight: 600 as const,
  color: 'var(--admin-text-dim)',
};
const thStyleRight = { ...thStyle, textAlign: 'right' as const };
const tdStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--admin-border)',
  fontSize: 14,
};
const tdStyleRight = { ...tdStyle, textAlign: 'right' as const };

const publishBtnStyle = {
  background: 'var(--admin-accent, #d98220)',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600 as const,
  cursor: 'pointer',
};
const cancelBtnStyle = {
  background: 'transparent',
  color: 'var(--admin-text)',
  border: '1px solid var(--admin-border)',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 14,
  cursor: 'pointer',
};
const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--admin-border)',
  borderRadius: 4,
  fontSize: 14,
  background: 'var(--admin-surface)',
  color: 'var(--admin-text)',
  boxSizing: 'border-box' as const,
};
const errorBoxStyle = {
  background: 'var(--admin-error-bg, #fee)',
  color: 'var(--admin-error, #c0392b)',
  padding: 8,
  borderRadius: 4,
  marginBottom: 8,
  fontSize: 14,
};
const modalBackdropStyle = {
  position: 'fixed' as const,
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
};
const modalBoxStyle = {
  background: 'var(--admin-surface, #fff)',
  borderRadius: 8,
  padding: 24,
  maxWidth: 720,
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
};
